import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormLabel,
  CFormSelect,
  CRow,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { cilCheckCircle, cilPeople, cilPlus, cilWarning } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  InfoBox,
  MiniStepper,
  StatTile,
} from 'src/components/common/ImportWizard'
import PlayHQService from 'src/service/PlayHQService'
import { DISMISSAL_OPTIONS } from '../ScoreCard/scorecardConfig'
import {
  FixtureSelect,
  STATUS_COLOR,
  fixtureLabel,
  formatDate,
  roundOrder,
  sameDayFixture,
  statusLabel,
} from './FixturesImporter'
import {
  CHECK_CELL_STYLE,
  FilterPills,
  LinkedViewTabs,
  LoadingBlock,
  MatchBadge,
  ROW_TONE,
  RecordSelect,
  errorsOf,
  matchRecord,
  optionsOf,
  rowStyle,
  tableHeadStyle,
} from './playhqShared'

const STEPS = ['Choose Fixtures', 'Validate Players', 'Review & Import']

const CHECKBOX_STYLE = { display: 'block', width: 18, height: 18 }

const NO_FILTERS = { team: '', grade: '', round: '', ourRound: '', status: '', fixture: '' }

// What each filter compares. `ourRound` is '' for a match no round of ours covers.
const FILTER_VALUE = {
  team: (c) => c.item.team_name,
  grade: (c) => c.item.grade_name,
  round: (c) => c.item.round_short || c.item.round_name,
  ourRound: (c) => (c.item.round_number ? String(c.item.round_number) : 'none'),
  status: (c) => c.item.status,
  fixture: (c) => (c.fixture_id ? 'has' : 'none'),
}

const HOW_OUT = Object.fromEntries(
  DISMISSAL_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
)

const playerLabel = (p) =>
  `${p.full_name}${p.team_name ? ` · ${p.team_name}` : ''}${
    Number(p.is_active) === 0 ? ' (inactive)' : ''
  }`

const has = (v) => v !== '' && v !== null && v !== undefined

// One innings of a player's figures, the way a scorecard reads.
const statLine = (s) => {
  if (!s) return null
  const parts = []
  if (has(s.rs) || has(s.bf)) {
    const how = s.no ? 'not out' : HOW_OUT[s.hwot] || ''
    parts.push(
      `${s.rs || 0}${s.no ? '*' : ''} (${s.bf || 0})${how && !s.no ? ` ${how}` : ''}${
        s.dks ? ' · duck' : ''
      }`,
    )
  }
  if (has(s.overs) && Number(s.overs) > 0)
    parts.push(`${s.overs}-${s.mdns || 0}-${s.run || 0}-${s.wks || 0}`)
  const field = [
    [s.cs, 'ct'],
    [s.cwks, 'ct (wk)'],
    [s.sts, 'st'],
    [s.rods, 'ro'],
    [s.roas, 'ro (assist)'],
  ]
    .filter(([v]) => Number(v) > 0)
    .map(([v, l]) => `${v} ${l}`)
  if (field.length) parts.push(field.join(', '))
  return parts.length ? parts.join(' · ') : 'did not bat or bowl'
}

const splitName = (name) => {
  const [first, ...rest] = String(name || '')
    .trim()
    .split(/\s+/)
  return [first || '', rest.join(' ')]
}

// Already linked: the fixture already has its squad (Squads tab) or figures (Scores tab) - from
// an earlier import or entered by hand. It can still be imported again.
const alreadyDone = (fixture, withScores) => {
  if (!fixture) return false
  return withScores ? Boolean(fixture.has_scores) : Number(fixture.squad_size) > 0
}

// In a league, the players of the team this PlayHQ player played for; else every player.
const playersFor = (ours, row, league) => {
  if (!league || !row.team_id) return ours
  const team = ours.filter((p) => String(p.team_id) === String(row.team_id))
  return team.length ? team : ours
}

// What a row's player select lists: its own team's players in a league, plus the one picked.
const choicesFor = (ours, row, league) => {
  const narrowed = playersFor(ours, row, league)
  const picked = ours.find((p) => String(p.id) === String(row.player_id))
  return picked && !narrowed.includes(picked) ? [picked, ...narrowed] : narrowed
}

/**
 * Squads or scores for fixtures, from PlayHQ. The admin chooses the fixtures, says who each
 * PlayHQ player is in this game (or adds them), and the import puts them in each fixture's
 * squad and - on the Scores tab (`withScores`) - also saves PlayHQ's figures through the normal
 * scorecard save, so fantasy points are worked out as usual. A scorecard needs its squad, so
 * scores always fill the squad too. Fixture status is not touched.
 *
 * In a league both sides are imported, each into its own team's squad, and a PlayHQ player is
 * matched against the players of the team they played for - a league squad only takes those.
 */
const ScoresImporter = ({ scope, teamIds, league = false, withScores = false, onImported }) => {
  const [step, setStep] = useState(1)
  const [maxStep, setMaxStep] = useState(1)

  // step 1
  const [fixtureData, setFixtureData] = useState(null)
  const [choices, setChoices] = useState([])
  const [loadErrors, setLoadErrors] = useState([])

  // step 2
  const [scoreData, setScoreData] = useState(null)
  const [mapRows, setMapRows] = useState([])
  const [loadingScores, setLoadingScores] = useState(false)
  const [filter, setFilter] = useState('all')

  // step 3
  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  const [result, setResult] = useState(null)

  const goTo = (n) => {
    setStep(n)
    setMaxStep((m) => Math.max(m, n))
  }

  const loadFixtures = useCallback(() => {
    setFixtureData(null)
    setLoadErrors([])
    setResult(null)
    setStep(1)
    setMaxStep(1)
    PlayHQService.preview('fixtures', { scope, team_ids: teamIds })
      .then((res) => {
        if (!res.success) {
          setLoadErrors(errorsOf(res))
          return
        }
        const ours = res.data.our_fixtures || []
        setFixtureData(res.data)
        setChoices(
          (res.data.items || []).map((item) => {
            const guess =
              item.fixture_id ||
              sameDayFixture(item, ours, item.team_id, league ? item.team2_id : null)?.id
            return {
              key: item.match_id,
              item,
              fixture_id: guess ? String(guess) : '',
              // Completed matches start ticked, unless this tab already has them.
              selected:
                Boolean(guess) &&
                (scope.type === 'match' ||
                  (item.status === 'COMPLETED' &&
                    !alreadyDone(
                      ours.find((f) => String(f.id) === String(guess)),
                      withScores,
                    ))),
            }
          }),
        )
      })
      .catch(() => setLoadErrors(['PlayHQ matches could not be loaded. Please try again.']))
  }, [scope, teamIds, league, withScores])

  useEffect(() => {
    loadFixtures()
  }, [loadFixtures])

  // A different set of fixtures means steps 2 and 3 have to be built again.
  useEffect(() => {
    setMaxStep(1)
  }, [choices])

  const selected = choices.filter((c) => c.selected && c.fixture_id)

  const ourFixtures = fixtureData?.our_fixtures || []
  const fixtureById = (id) => ourFixtures.find((f) => String(f.id) === String(id))

  const isDone = (c) => Boolean(c.fixture_id) && alreadyDone(fixtureById(c.fixture_id), withScores)
  const [view, setView] = useState('pending')
  const viewCounts = { pending: 0, linked: 0 }
  choices.forEach((c) => viewCounts[isDone(c) ? 'linked' : 'pending']++)

  // Filters and the view only narrow what is shown. A match ticked and then filtered out stays
  // ticked, and the footer says how many of those there are.
  const [chooseFilters, setChooseFilters] = useState(NO_FILTERS)
  const filtering = Object.values(chooseFilters).some(Boolean)
  const visibleChoices = choices.filter(
    (c) =>
      isDone(c) === (view === 'linked') &&
      Object.entries(chooseFilters).every(
        ([key, value]) => !value || FILTER_VALUE[key](c) === value,
      ),
  )
  const selectable = visibleChoices.filter((c) => c.fixture_id)
  const allSelected = selectable.length > 0 && selectable.every((c) => c.selected)
  const hiddenSelected = selected.filter((c) => !visibleChoices.includes(c)).length

  const filterOptions = useMemo(
    () => ({
      team: optionsOf(choices, FILTER_VALUE.team).sort((a, b) => a.value.localeCompare(b.value)),
      grade: optionsOf(choices, FILTER_VALUE.grade).sort((a, b) => a.value.localeCompare(b.value)),
      round: optionsOf(choices, FILTER_VALUE.round).sort(
        (a, b) => roundOrder(a.value) - roundOrder(b.value) || a.value.localeCompare(b.value),
      ),
      ourRound: optionsOf(choices, FILTER_VALUE.ourRound).sort((a, b) =>
        a.value === 'none' ? 1 : b.value === 'none' ? -1 : Number(a.value) - Number(b.value),
      ),
      status: optionsOf(choices, FILTER_VALUE.status).sort((a, b) =>
        a.value.localeCompare(b.value),
      ),
      fixture: optionsOf(choices, FILTER_VALUE.fixture),
    }),
    [choices],
  )

  const updateChoice = (key, changes) =>
    setChoices((current) => current.map((c) => (c.key === key ? { ...c, ...changes } : c)))

  /* ---------------------------- step 2 ---------------------------- */

  const loadScores = async () => {
    setLoadingScores(true)
    setLoadErrors([])
    try {
      const res = await PlayHQService.preview('scores', {
        pairs: selected.map((c) => ({ fixture_id: c.fixture_id, match_id: c.item.match_id })),
      })
      if (!res.success) {
        setLoadErrors(errorsOf(res))
        return null
      }
      return res.data
    } catch (e) {
      setLoadErrors(['Scorecards could not be read from PlayHQ. Please try again.'])
      return null
    } finally {
      setLoadingScores(false)
    }
  }

  const startValidate = async () => {
    const data = await loadScores()
    if (!data) return
    setScoreData(data)

    // One row per PlayHQ player across every chosen fixture: who they are is the same answer
    // in each match.
    const ours = data.our_players || []
    const byParticipant = new Map()
    data.items
      .filter((item) => !item.error)
      .forEach((item) => {
        item.players.forEach((p) => {
          const row = byParticipant.get(p.participant_id) || {
            key: p.participant_id,
            participant_id: p.participant_id,
            name: p.name,
            is_keeper: p.is_keeper,
            // The team a new player joins: the side they played for (in a club, the fixture's).
            team_id: p.team_id ? String(p.team_id) : '',
            side_name: p.side_name,
            fixture_ids: [],
          }
          row.fixture_ids.push(item.fixture_id)
          row.is_keeper = row.is_keeper || p.is_keeper
          byParticipant.set(p.participant_id, row)
        })
      })

    setMapRows(
      [...byParticipant.values()]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((row) => {
          // Already linked by PlayHQ id wherever they are; by name only in their own team in a
          // league.
          const linked = ours.find((r) => r.playhq_id && r.playhq_id === row.participant_id)
          const found = linked
            ? { record: linked, how: 'linked' }
            : matchRecord(
                playersFor(ours, row, league),
                row.participant_id,
                row.name,
                (r) => r.full_name,
              )
          return {
            ...row,
            how: found.how,
            action: found.how === 'none' ? 'create' : 'link',
            player_id: found.record ? String(found.record.id) : '',
          }
        }),
    )
    setFilter('all')
    goTo(2)
  }

  const updateMapRow = (key, changes) =>
    setMapRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row
        const next = { ...row, ...changes }
        if ('player_id' in changes) next.how = changes.player_id ? 'manual' : 'none'
        return next
      }),
    )

  const checkedMap = useMemo(() => {
    const used = new Map()
    mapRows.forEach((row) => {
      if (row.action === 'link' && row.player_id)
        used.set(row.player_id, (used.get(row.player_id) || 0) + 1)
    })
    const ours = scoreData?.our_players || []
    return mapRows.map((row) => {
      let problem = null
      const player = row.player_id && ours.find((p) => String(p.id) === String(row.player_id))
      if (row.action === 'link' && !row.player_id)
        problem = 'Choose which of your players this is, or add them.'
      else if (row.action === 'link' && used.get(row.player_id) > 1)
        problem = 'Another PlayHQ player is mapped to the same player.'
      else if (
        league &&
        row.action === 'link' &&
        row.team_id &&
        player &&
        String(player.team_id) !== row.team_id
      )
        problem = `${player.full_name} is in ${player.team_name || 'no team'}, but played for ${
          row.side_name
        } here. A league squad only takes the team's own players - pick a ${
          row.side_name
        } player, add them as new, or move them on the Players page.`
      return {
        ...row,
        problem,
        tone: row.action === 'skip' ? 'skipped' : problem ? 'attention' : 'ready',
      }
    })
  }, [mapRows, scoreData, league])

  const mapCounts = useMemo(() => {
    const c = { all: checkedMap.length, attention: 0, create: 0, link: 0, skip: 0 }
    checkedMap.forEach((row) => {
      if (row.tone === 'attention') c.attention++
      c[row.action]++
    })
    return c
  }, [checkedMap])

  /* ---------------------------- step 3 ---------------------------- */

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const teamOfFixture = (fixtureId) => fixtureById(fixtureId)?.team || null

      // 1. Players first: add the new ones, and remember the PlayHQ id of those just matched by
      //    name, so the next import finds them straight away.
      const playerRows = checkedMap
        .filter((row) => row.action === 'create' || (row.action === 'link' && row.how !== 'linked'))
        .map((row) => {
          const [first, last] = splitName(row.name)
          return {
            participant_id: row.participant_id,
            first_name: first,
            last_name: last,
            is_keeper: row.is_keeper,
            action: row.action,
            player_id: row.action === 'link' ? row.player_id : null,
            team_id: row.team_id || teamOfFixture(row.fixture_ids[0]),
          }
        })

      let ourPlayers = scoreData.our_players || []
      if (playerRows.length) {
        const res = await PlayHQService.runImport('players', { rows: playerRows })
        if (!res.success) {
          setImportErrors(errorsOf(res))
          return
        }
        const fresh = await loadScores()
        if (!fresh) return
        ourPlayers = fresh.our_players || []
      }

      const playerFor = new Map(
        ourPlayers.filter((p) => p.playhq_id).map((p) => [p.playhq_id, String(p.id)]),
      )
      checkedMap.forEach((row) => {
        if (row.action === 'link' && row.player_id) playerFor.set(row.participant_id, row.player_id)
      })
      const skipped = new Set(
        checkedMap.filter((r) => r.action === 'skip').map((r) => r.participant_id),
      )

      // 2. Squads and scores, fixture by fixture.
      const fixtures = scoreData.items
        .filter((item) => !item.error)
        .map((item) => ({
          fixture_id: item.fixture_id,
          match_id: item.match_id,
          import_scores: withScores,
          players: item.players
            .filter((p) => !skipped.has(p.participant_id) && playerFor.has(p.participant_id))
            .map((p) => ({
              participant_id: p.participant_id,
              player_id: playerFor.get(p.participant_id),
            })),
        }))

      const res = await PlayHQService.runImport('scores', { fixtures })
      if (res.data?.fixtures?.length) {
        setResult(res.data.fixtures)
        ToastComponent(res.message || 'Imported.', 'success')
        if (onImported) onImported()
      }
      if (!res.success) setImportErrors(errorsOf(res))
    } catch (e) {
      setImportErrors(['The import could not be run. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  /* ---------------------------- render ---------------------------- */

  if (!fixtureData && !loadErrors.length)
    return <LoadingBlock text="Loading matches from PlayHQ…" />

  if (result) {
    return (
      <>
        <InfoBox color="success">
          <div className="fw-semibold mb-1">Imported:</div>
          <ul className="mb-0 ps-3">
            {result.map((r) => (
              <li key={r.fixture_id}>
                {r.label} - {r.squad} players in the squad
                {r.innings_scored ? `, scores for ${r.innings_scored} innings` : ', no scores'}
              </li>
            ))}
          </ul>
        </InfoBox>
        <ImportErrorAlert errors={importErrors} title="Some fixtures were not imported:" />
        <div className="small text-medium-emphasis mb-3">
          Check each scorecard and mark the fixture complete when you are happy with it.
        </div>
        <CButton color="primary" variant="outline" onClick={loadFixtures}>
          Import more
        </CButton>
      </>
    )
  }

  const filterSelect = (key, label, allLabel, describe = (v) => v) => (
    <CCol xs={12} sm={6} lg>
      <CFormLabel className="small fw-semibold mb-1">{label}</CFormLabel>
      <CFormSelect
        size="sm"
        value={chooseFilters[key]}
        onChange={(e) => setChooseFilters((f) => ({ ...f, [key]: e.target.value }))}
      >
        <option value="">{allLabel}</option>
        {filterOptions[key].map((o) => (
          <option key={o.value} value={o.value}>
            {describe(o.value)} ({o.count})
          </option>
        ))}
      </CFormSelect>
    </CCol>
  )

  const renderFilters = () => (
    <div className="border rounded-4 p-3 mb-3" style={tableHeadStyle}>
      <CRow className="g-2 align-items-end">
        {league
          ? filterSelect('grade', 'Grade', 'All grades')
          : filterSelect('team', 'Team', 'All teams')}
        {filterSelect('round', 'PlayHQ round', 'All PlayHQ rounds')}
        {filterSelect('ourRound', 'Your round', 'All your rounds', (v) =>
          v === 'none' ? 'No round' : `Round ${v}`,
        )}
        {filterSelect('status', 'Match status', 'Any status', statusLabel)}
        {filterSelect('fixture', 'Your fixture', 'With or without', (v) =>
          v === 'has' ? 'Has a fixture' : 'Needs a fixture',
        )}
        <CCol xs="auto">
          <CButton
            size="sm"
            color="secondary"
            variant="ghost"
            disabled={!filtering}
            onClick={() => setChooseFilters(NO_FILTERS)}
          >
            Clear filters
          </CButton>
        </CCol>
      </CRow>
      <div className="small text-medium-emphasis mt-2">
        Showing {visibleChoices.length} of {viewCounts[view]} matches. PlayHQ round numbers differ
        between grades - pick a team too, or use Your round to get one weekend across every team.
      </div>
    </div>
  )

  const renderChoose = () => (
    <>
      <InfoBox color="light">
        {withScores
          ? 'Choose the fixtures to fill scorecards for from PlayHQ.'
          : 'Choose the fixtures to create squads for from PlayHQ.'}{' '}
        A match needs one of your fixtures - import it on the Fixtures tab first, or pick the
        fixture you made by hand.
        {withScores &&
          ' Players not yet in a fixture’s squad are added to it, as a scorecard needs its squad.'}
      </InfoBox>
      <ImportErrorAlert errors={loadErrors} title="PlayHQ data could not be loaded:" />
      {choices.length > 0 && (
        <LinkedViewTabs
          value={view}
          onChange={setView}
          counts={viewCounts}
          noun={withScores ? 'scorecards' : 'squads'}
        />
      )}
      {choices.length > 0 && renderFilters()}
      {!choices.length ? (
        <InfoBox color="light">PlayHQ has no matches here.</InfoBox>
      ) : !viewCounts[view] ? (
        <InfoBox color="light">
          {view === 'linked'
            ? `No ${withScores ? 'scorecards' : 'squads'} imported yet.`
            : `Every fixture already has its ${withScores ? 'scores' : 'squad'}.`}
        </InfoBox>
      ) : !visibleChoices.length ? (
        <InfoBox color="light">No matches fit these filters.</InfoBox>
      ) : (
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <CTable align="middle" hover className="mb-0">
              <CTableHead className="align-middle" style={tableHeadStyle}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3" style={CHECK_CELL_STYLE}>
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      style={CHECKBOX_STYLE}
                      title={
                        filtering
                          ? 'Select every shown match that has a fixture'
                          : 'Select every match that has a fixture'
                      }
                      checked={allSelected}
                      disabled={!selectable.length}
                      onChange={(e) => {
                        const shown = new Set(visibleChoices.map((c) => c.key))
                        setChoices((cs) =>
                          cs.map((c) =>
                            shown.has(c.key)
                              ? { ...c, selected: e.target.checked && Boolean(c.fixture_id) }
                              : c,
                          ),
                        )
                      }}
                    />
                  </CTableHeaderCell>
                  <CTableHeaderCell>PlayHQ match</CTableHeaderCell>
                  <CTableHeaderCell>Your fixture</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {visibleChoices.map((c) => (
                  <CTableRow
                    key={c.key}
                    style={{
                      borderLeft: `3px solid ${
                        c.selected && c.fixture_id ? ROW_TONE.ready : 'transparent'
                      }`,
                    }}
                  >
                    <CTableDataCell className="ps-3">
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        style={{
                          ...CHECKBOX_STYLE,
                          cursor: c.fixture_id ? 'pointer' : 'not-allowed',
                        }}
                        title={c.fixture_id ? '' : 'Choose a fixture first'}
                        checked={c.selected && Boolean(c.fixture_id)}
                        disabled={!c.fixture_id}
                        onChange={(e) => updateChoice(c.key, { selected: e.target.checked })}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="fw-semibold">
                        {c.item.round_short} · {c.item.team_name} vs {c.item.opponent}
                      </div>
                      <div className="small text-medium-emphasis">
                        {formatDate(c.item.start_date, c.item.start_time)} ·{' '}
                        {c.item.result || c.item.status}
                      </div>
                      <div className="d-flex gap-1 mt-1 flex-wrap">
                        <CBadge
                          color={STATUS_COLOR[c.item.status] || 'secondary'}
                          className="fw-normal"
                        >
                          {c.item.status}
                        </CBadge>
                        {c.item.round_number ? (
                          <CBadge color="light" textColor="dark" className="fw-normal">
                            Your round {c.item.round_number}
                          </CBadge>
                        ) : (
                          <CBadge color="warning" className="fw-normal">
                            No round
                          </CBadge>
                        )}
                        {isDone(c) && (
                          <CBadge color="success" shape="rounded-pill" className="fw-normal">
                            {withScores
                              ? 'Scores imported'
                              : `Squad imported · ${fixtureById(c.fixture_id).squad_size} players`}
                          </CBadge>
                        )}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell>
                      {c.item.fixture_id ? (
                        <span className="small">
                          {fixtureById(c.fixture_id)
                            ? fixtureLabel(fixtureById(c.fixture_id))
                            : 'Imported fixture'}
                        </span>
                      ) : (
                        <FixtureSelect
                          value={c.fixture_id}
                          onChange={(fixture_id) =>
                            updateChoice(c.key, { fixture_id, selected: Boolean(fixture_id) })
                          }
                          fixtures={ourFixtures.filter((f) => !f.playhq_id)}
                          roundId={c.item.round_id}
                          placeholder="Not imported - choose a fixture…"
                        />
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
      )}
      <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
        {hiddenSelected > 0 && (
          <span className="small text-medium-emphasis me-auto">
            {hiddenSelected} selected {hiddenSelected === 1 ? 'match is' : 'matches are'} not shown
            here but will still be imported.
          </span>
        )}
        <CButton color="secondary" variant="outline" onClick={loadFixtures}>
          Reload from PlayHQ
        </CButton>
        <CLoadingButton
          color="primary"
          disabled={!selected.length}
          loading={loadingScores}
          onClick={startValidate}
        >
          Next: {selected.length} {selected.length === 1 ? 'fixture' : 'fixtures'}
        </CLoadingButton>
      </div>
    </>
  )

  const renderValidate = () => {
    const ours = scoreData?.our_players || []
    const visible = checkedMap.filter((row) => {
      if (filter === 'attention') return row.tone === 'attention'
      if (filter === 'create') return row.action === 'create'
      if (filter === 'link') return row.action === 'link'
      if (filter === 'skip') return row.action === 'skip'
      return true
    })
    return (
      <>
        <InfoBox color="light">
          Say who each PlayHQ player is in your game. Players not in your game can be added (as
          batters - or WK - at your lowest price) or left out of the squad.
          {league &&
            ' Both sides are listed; each player is matched with, and added to, the team they played for.'}
        </InfoBox>
        <div className="d-flex gap-3 flex-wrap mb-3">
          <StatTile
            label="Matched"
            value={mapCounts.link - mapCounts.attention}
            tone="success"
            icon={cilCheckCircle}
          />
          <StatTile
            label="Needs attention"
            value={mapCounts.attention}
            tone="warning"
            icon={cilWarning}
          />
          <StatTile label="Will be added" value={mapCounts.create} tone="info" icon={cilPlus} />
        </div>
        <FilterPills
          value={filter}
          onChange={setFilter}
          filters={[
            { key: 'all', label: 'All', count: mapCounts.all },
            { key: 'attention', label: 'Needs attention', count: mapCounts.attention },
            { key: 'link', label: 'Matched', count: mapCounts.link },
            { key: 'create', label: 'Add new', count: mapCounts.create },
            { key: 'skip', label: 'Left out', count: mapCounts.skip },
          ]}
        />
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <CTable align="middle" hover className="mb-0">
              <CTableHead className="align-middle" style={tableHeadStyle}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3">PlayHQ player</CTableHeaderCell>
                  {league && <CTableHeaderCell>Played for</CTableHeaderCell>}
                  <CTableHeaderCell>Fixtures</CTableHeaderCell>
                  <CTableHeaderCell>Action</CTableHeaderCell>
                  <CTableHeaderCell>Your player</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {visible.map((row) => (
                  <CTableRow key={row.key} style={rowStyle(row.tone)}>
                    <CTableDataCell className="ps-3">
                      <div className="fw-semibold">
                        {row.name}{' '}
                        {row.is_keeper && (
                          <CBadge color="dark" className="fw-normal">
                            WK
                          </CBadge>
                        )}
                      </div>
                      <MatchBadge how={row.how} />
                      {row.problem && <div className="small text-warning mt-1">{row.problem}</div>}
                    </CTableDataCell>
                    {league && <CTableDataCell className="small">{row.side_name}</CTableDataCell>}
                    <CTableDataCell>{row.fixture_ids.length}</CTableDataCell>
                    <CTableDataCell>
                      <select
                        className="form-select form-select-sm"
                        style={{ minWidth: 170 }}
                        value={row.action}
                        onChange={(e) => updateMapRow(row.key, { action: e.target.value })}
                      >
                        <option value="link">Existing player</option>
                        <option value="create">Add as new player</option>
                        <option value="skip">Leave out</option>
                      </select>
                    </CTableDataCell>
                    <CTableDataCell>
                      {row.action === 'link' ? (
                        <RecordSelect
                          value={row.player_id}
                          onChange={(player_id) => updateMapRow(row.key, { player_id })}
                          records={choicesFor(ours, row, league)}
                          getLabel={playerLabel}
                          placeholder="Choose player…"
                          invalid={!row.player_id}
                        />
                      ) : row.action === 'create' ? (
                        <span className="small text-medium-emphasis">New: {row.name}</span>
                      ) : (
                        '—'
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <div className="d-flex justify-content-end gap-2">
          <CButton color="secondary" variant="outline" onClick={() => setStep(1)}>
            Back
          </CButton>
          <CButton color="primary" disabled={mapCounts.attention > 0} onClick={() => goTo(3)}>
            Review
          </CButton>
        </div>
      </>
    )
  }

  const renderReview = () => {
    const ours = scoreData?.our_players || []
    const rowFor = new Map(checkedMap.map((r) => [r.participant_id, r]))
    const ourName = (row) => {
      if (!row || row.action === 'skip') return null
      if (row.action === 'create') return `${row.name} (new)`
      return ours.find((p) => String(p.id) === String(row.player_id))?.full_name || '—'
    }

    return (
      <>
        <div className="d-flex gap-3 flex-wrap mb-3">
          <StatTile
            label="Fixtures"
            value={scoreData.items.filter((i) => !i.error).length}
            tone="info"
            icon={cilPeople}
          />
          <StatTile label="New players" value={mapCounts.create} tone="success" icon={cilPlus} />
          <StatTile label="Left out" value={mapCounts.skip} tone="secondary" icon={cilWarning} />
        </div>
        {!withScores && <InfoBox color="light">Only squads will be imported - no scores.</InfoBox>}
        {scoreData.items.map((item) => {
          if (item.error) {
            return (
              <InfoBox key={item.fixture_id} color="warning">
                {fixtureById(item.fixture_id)
                  ? fixtureLabel(fixtureById(item.fixture_id))
                  : `Fixture ${item.fixture_id}`}
                : {item.error} It will be skipped.
              </InfoBox>
            )
          }
          const included = item.players.filter(
            (p) => rowFor.get(p.participant_id)?.action !== 'skip',
          )
          const overwriting = withScores
            ? new Set(
                included
                  .map((p) => rowFor.get(p.participant_id))
                  .filter((r) => r?.action === 'link')
                  .filter((r) =>
                    Object.values(item.scored || {}).some((ids) =>
                      ids.map(String).includes(String(r.player_id)),
                    ),
                  )
                  .map((r) => r.participant_id),
              )
            : new Set()

          return (
            <CCard key={item.fixture_id} className="border-secondary-subtle rounded-4 mb-3">
              <CCardHeader className="bg-transparent">
                <div className="fw-semibold">{item.fixture_label}</div>
                <div className="small text-medium-emphasis">
                  PlayHQ: {item.match_label} · {item.result || item.match_status}
                </div>
              </CCardHeader>
              <CCardBody>
                {withScores && !item.has_scorecard && (
                  <InfoBox color="warning">
                    PlayHQ has no scorecard for this match yet - only the squad will be imported.
                  </InfoBox>
                )}
                {withScores && item.extra_innings?.length > 0 && (
                  <InfoBox color="warning">
                    PlayHQ has a second innings but this is a 1-Day fixture - only the first innings
                    is imported.
                  </InfoBox>
                )}
                {overwriting.size > 0 && (
                  <InfoBox color="warning">
                    {overwriting.size}{' '}
                    {overwriting.size === 1 ? 'player already has' : 'players already have'} scores
                    on this fixture. They will be replaced with PlayHQ&rsquo;s.
                  </InfoBox>
                )}
                <div className="table-responsive">
                  <CTable small align="middle" className="mb-0">
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>PlayHQ player</CTableHeaderCell>
                        <CTableHeaderCell>Your player</CTableHeaderCell>
                        {withScores &&
                          item.innings.map((n) => (
                            <CTableHeaderCell key={n}>
                              {item.innings.length > 1 ? `Innings ${n}` : 'Figures'}
                            </CTableHeaderCell>
                          ))}
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {item.players.map((p) => {
                        const row = rowFor.get(p.participant_id)
                        const name = ourName(row)
                        return (
                          <CTableRow key={p.participant_id} className={name ? '' : 'opacity-50'}>
                            <CTableDataCell>
                              {p.name}
                              {league && (
                                <span className="small text-medium-emphasis"> · {p.side_name}</span>
                              )}
                              {overwriting.has(p.participant_id) && (
                                <CBadge color="warning" className="fw-normal ms-1">
                                  replaces scores
                                </CBadge>
                              )}
                            </CTableDataCell>
                            <CTableDataCell>{name || 'Left out'}</CTableDataCell>
                            {withScores &&
                              item.innings.map((n) => (
                                <CTableDataCell key={n} className="small">
                                  {p.innings?.[n] ? statLine(p.innings[n]) : '—'}
                                </CTableDataCell>
                              ))}
                          </CTableRow>
                        )
                      })}
                    </CTableBody>
                  </CTable>
                </div>
                <div className="small text-medium-emphasis mt-2">
                  {included.length} players go into the squad.
                </div>
              </CCardBody>
            </CCard>
          )
        })}
        <ImportErrorAlert errors={importErrors} />
        <div className="d-flex justify-content-end gap-2">
          <CButton color="secondary" variant="outline" onClick={() => setStep(2)}>
            Back
          </CButton>
          <CLoadingButton color="primary" loading={importing} onClick={runImport}>
            {withScores ? 'Import scores' : 'Import squads'}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <MiniStepper steps={STEPS} current={step} maxReached={maxStep} onStepClick={setStep} />
      {step === 1 && renderChoose()}
      {step === 2 && scoreData && renderValidate()}
      {step === 3 && scoreData && renderReview()}
    </>
  )
}

export default ScoresImporter
